'use client'

import useStopFilterManager from '@/src/hooks/srp/stopFilterManager'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { Box, Typography } from '@mui/material'
import { useLocale } from 'next-intl'
import Image from 'next/image'
import React, { createContext, useLayoutEffect, useRef, useState } from 'react'
import tell from './tell.png'

const BADGE_GAP = 8

export interface TopSortCardsBadgeLayout {
    left: number
    height: number
}

export const TopSortedBadgesWidthContext = createContext<TopSortCardsBadgeLayout>({
    left: 0,
    height: 0
})

interface TopSortedCardsLabelProps {
    children: React.ReactNode
    label: string
    isActive: boolean
    sortOptionId: string
    isMobile: boolean
    isCombined?: boolean
}

const CHEAPEST_COLOR = '#0FA670'
const QUICKEST_COLOR = '#3B82F6'
const EARLY_DEPARTURE_COLOR = '#F59E0B'
const LATE_DEPARTURE_COLOR = '#EF4444'

const colorMap = {
    cheapest: CHEAPEST_COLOR,
    quickest: QUICKEST_COLOR,
    earlyMorning: EARLY_DEPARTURE_COLOR,
    lateNight: LATE_DEPARTURE_COLOR
}


const TopSortCardsBadge: React.FC<TopSortedCardsLabelProps> = ({
    children,
    isActive,
    label,
    sortOptionId,
    isMobile,
    isCombined
}) => {

    const locale = useLocale()
    const isArabic = locale === 'ar'
    const badgeRef = useRef<HTMLDivElement>(null)
    const [badgeLayout, setBadgeLayout] = useState<TopSortCardsBadgeLayout>({ left: 0, height: 0 })

    const measureBadge = React.useCallback(() => {
        if (badgeRef.current) {
            // offsetWidth is the visible width; scrollWidth is the full content width.
            // Use scrollWidth so downstream layout (promo positioning) remains stable
            // even when we enable horizontal scrolling on mobile.
            const { scrollWidth, offsetWidth, offsetHeight } = badgeRef.current
            const measuredWidth = scrollWidth > 0 ? scrollWidth : offsetWidth
            setBadgeLayout({
                left: measuredWidth > 0 ? measuredWidth - 10 + BADGE_GAP : 0,
                height: offsetHeight
            })
        }
    }, [])

    useLayoutEffect(() => {
        measureBadge()
        const el = badgeRef.current
        if (!el) return
        const observer = new ResizeObserver(measureBadge)
        observer.observe(el)
        return () => observer.disconnect()
    }, [label, isCombined, locale, measureBadge])
    const { nonStopChecked: isOnlyNonStopSelected } = useStopFilterManager()
    const { selectedSortForSelectedJourneyLeg } = useSearchResults()
    const isSmartSort = selectedSortForSelectedJourneyLeg?.sortKey === 'smart'
    const hideBadge = isSmartSort
    const showFastestBadge = !isOnlyNonStopSelected

    const ids = isCombined ? sortOptionId.split('_') : [sortOptionId]
    const labels = isCombined ? label.split('_') : [label]
    const fastestIdIndex = ids.indexOf("quickest")
    const availableIds = showFastestBadge ? ids : ids.filter(id => id !== "quickest")
    const availableLabels = showFastestBadge ? labels : labels.filter((_, index) => index !== fastestIdIndex)

    if (availableIds.length === 0 || hideBadge) return children
    // return children

    return (
        <TopSortedBadgesWidthContext.Provider value={badgeLayout}>
            <Box position={"relative"}>
                <Image src={tell} alt='tell' width={10} height={10} style={{
                    position: "absolute",
                    top: badgeLayout.height,
                    ...(isArabic ? { right: -10 } : { left: -10 }),
                    transform: isArabic ? 'scaleX(-1)' : 'none',

                    zIndex: 10,
                }} />
                <Box
                    ref={badgeRef}
                    display={'flex'}
                    justifyContent={'flex-start'}
                    position={"absolute"}
                    top={0}
                    left={-10}
                    zIndex={10}
                    gap={1}
                    sx={{
                        flexWrap: 'nowrap',
                        overflowY: 'hidden',
                        // When multiple sort badges land on a single card on mobile,
                        // avoid vertical growth by allowing horizontal scrolling instead.
                        overflowX: isMobile ? 'auto' : 'visible',
                        // Keep the scroll area bounded to the card width.
                        maxWidth: isMobile ? 'calc(100% + 10px)' : 'none',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' }
                    }}

                >
                    {availableIds?.map((id, index) => {
                        return (
                            <Box
                                key={id}
                                bgcolor={colorMap[id as keyof typeof colorMap]}
                                // width={'100px'}
                                px={1}
                                flexShrink={0}
                                sx={{
                                    minWidth: 'max-content',
                                    borderRadius: "4px",
                                    ...(index === 0 && {
                                        borderBottomLeftRadius: 0,
                                        borderTopLeftRadius: 0
                                    }),
                                    ...(index !== 0 && {
                                        borderTopLeftRadius: 0,
                                        borderBottomLeftRadius: 0
                                    })
                                }}

                            >
                                <Typography
                                    variant="body2"
                                    color={'white'}
                                    textAlign={'center'}
                                    fontWeight={600}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {availableLabels[index]}
                                </Typography>
                            </Box>
                        )
                    })}
                </Box>

                <Box
                    borderRadius={isMobile ? 2 : 1}
                >
                    {children}
                </Box>
            </Box>
        </TopSortedBadgesWidthContext.Provider>
    )
}

export default TopSortCardsBadge
